<template>
  <div v-if="visibleAnnouncements.length > 0">
    <div
      v-for="announcement in visibleAnnouncements"
      :key="announcement.id"
      :style="{
        backgroundColor: announcement.background_color || 'rgba(0,0,0,0.04)',
        color: announcement.text_color || 'var(--p-text-color)',
      }"
      class="relative flex items-center justify-between px-4 py-2 text-sm font-medium backdrop-blur supports-[backdrop-filter]:bg-black/5"
    >
      <!-- Content -->
      <div
        class="flex-1 text-center opacity-90"
        v-html="announcement.content"
      />

      <!-- Close Button (only if closable) -->
      <button
        v-if="announcement.is_closable"
        class="ml-4 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-black/10 text-current opacity-70 transition hover:bg-black/20 hover:opacity-100"
        :aria-label="t('announcementBar.close')"
        @click="closeAnnouncement(announcement.id)"
      >
        <i class="pi pi-times text-xs" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Announcement } from '@stw/types';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { getActiveAnnouncements } from '@/api/announcement';

const { t } = useI18n();

const announcements = ref<Announcement[]>([]);
const closedAnnouncementIds = ref<string[]>([]);

/**
 * Initialize closed announcements from localStorage
 */
const initializeClosedAnnouncements = () => {
  closedAnnouncementIds.value = JSON.parse(
    localStorage.getItem('closedAnnouncements') || '[]'
  );
};

/**
 * Get all announcements to display, sorted by order (lowest first)
 */
const visibleAnnouncements = computed(() => {
  if (!announcements.value || announcements.value.length === 0) return [];

  // Filter out closed announcements (only closable ones can be closed)
  const availableAnnouncements = announcements.value.filter(
    (announcement) =>
      !announcement.is_closable ||
      !closedAnnouncementIds.value.includes(announcement.id)
  );

  return availableAnnouncements.sort((a, b) => a.order - b.order);
});

/**
 * Check if an announcement has been closed by the user
 */
const isClosedAnnouncement = (announcementId: string): boolean => {
  return closedAnnouncementIds.value.includes(announcementId);
};

/**
 * Close an announcement and save to localStorage
 */
const closeAnnouncement = (announcementId: string): void => {
  if (!closedAnnouncementIds.value.includes(announcementId)) {
    closedAnnouncementIds.value.push(announcementId);
    localStorage.setItem(
      'closedAnnouncements',
      JSON.stringify(closedAnnouncementIds.value)
    );
  }
};

/**
 * Load announcements from API
 */
const loadAnnouncements = async (): Promise<void> => {
  try {
    announcements.value = await getActiveAnnouncements();
  } catch (error) {
    console.error('Failed to load announcements:', error);
    announcements.value = [];
  }
};

// Handle reset announcements event from App.vue
const handleResetAnnouncements = () => {
  initializeClosedAnnouncements();
};

// Load announcements and initialize closed state on component mount
onMounted(() => {
  initializeClosedAnnouncements();
  loadAnnouncements();

  // Listen for reset event from App.vue
  window.addEventListener('resetAnnouncements', handleResetAnnouncements);
});

// Cleanup event listener
onUnmounted(() => {
  window.removeEventListener('resetAnnouncements', handleResetAnnouncements);
});
</script>

<style scoped>
/* Global styles for announcement content */
:deep(a) {
  text-decoration: underline;
  font-weight: 600;
}

:deep(a:hover) {
  opacity: 0.8;
}

:deep(strong) {
  font-weight: 700;
}

:deep(em) {
  font-style: italic;
}
</style>
